import {useRef, useState, useEffect, useCallback} from 'react'
import levels from '@cssbattle/shared/levels'
import styles from './BubbleLoader.module.css'
import {VStack} from './Stack'
import {Text} from './Text'
import {debounce} from 'src/utils'

let isActive = false,
  lastTime = 0,
  particles = [],
  particlePointer = 0,
  MAX_PARTICLES = 500,
  PARTICLE_NUM_RANGE = {min: 25, max: 50},
  PARTICLE_SIZE_RANGE = {min: 3, max: 10},
  PARTICLE_ALPHA_START = 0.5,
  PARTICLE_ALPHA_FADEOUT = 0.99,
  PARTICLE_SIZE_FADEOUT = 0.975,
  PARTICLE_VELOCITY_RANGE = {
    x: [-1, 1],
    y: [-3.5, -1.5],
  },
  w,
  h,
  ctx,
  effect = 2,
  imageRect

function random(min, max) {
  if (!max) {
    max = min
    min = 0
  }
  return min + ~~(Math.random() * (max - min + 1))
}

function getRGBComponentFromHex(hex) {
  const [r, g, b] = hex
    .toLowerCase()
    .match(/[a-f0-9]{2}/g)
    .map((x) => parseInt(x, 16))
  return [r, g, b]
}

function spawnParticles(ctx, colors) {
  var numParticles = random(PARTICLE_NUM_RANGE.min, PARTICLE_NUM_RANGE.max)
  for (var i = numParticles; i--; ) {
    const pos = {
      left: imageRect.left + random(0, imageRect.width),
      top: imageRect.top + random(0, imageRect.height),
    }
    var color = getRGBComponentFromHex(
      colors[Math.floor(Math.random() * colors.length)]
    )

    particles[particlePointer] = createParticle(pos.left, pos.top, color)
    particlePointer = (particlePointer + 1) % MAX_PARTICLES
  }
}

function createParticle(x, y, color) {
  var p = {
    x: x,
    y: y + 10,
    alpha: PARTICLE_ALPHA_START,
    color: color,
  }

  p.size = random(PARTICLE_SIZE_RANGE.min, PARTICLE_SIZE_RANGE.max)
  p.drag = 0.92
  p.vx = random(-3, 3)
  p.vy = random(-3, 3)
  p.wander = 0.15
  p.theta = (random(0, 360) * Math.PI) / 180

  return p
}

// Effect based on Soulwire's demo: http://codepen.io/soulwire/pen/foktm
function effect2(particle) {
  particle.x += particle.vx
  particle.y += particle.vy
  particle.vx *= particle.drag
  particle.vy *= particle.drag
  particle.theta += random(-0.5, 0.5)
  particle.vx += Math.sin(particle.theta) * 0.1
  particle.vy += Math.cos(particle.theta) * 0.1
  particle.size *= PARTICLE_SIZE_FADEOUT
  particle.alpha *= PARTICLE_ALPHA_FADEOUT

  ctx.fillStyle =
    'rgba(' +
    particle.color[0] +
    ',' +
    particle.color[1] +
    ',' +
    particle.color[2] +
    ',' +
    particle.alpha +
    ')'
  ctx.beginPath()
  ctx.arc(
    Math.round(particle.x - 1),
    Math.round(particle.y - 1),
    particle.size,
    0,
    2 * Math.PI
  )
  ctx.fill()
}

function drawParticles(timeDelta) {
  var particle
  for (var i = particles.length; i--; ) {
    particle = particles[i]
    if (!particle || particle.alpha < 0.01 || particle.size <= 0.5) {
      continue
    }

    if (effect === 1) {
      effect1(particle)
    } else if (effect === 2) {
      effect2(particle)
    }
  }
}

function loop() {
  if (!isActive) return
  ctx.clearRect(0, 0, w, h)

  // get the time past the previous frame
  var current_time = new Date().getTime()
  if (!lastTime) lastTime = current_time
  var dt = (current_time - lastTime) / 1000
  lastTime = current_time

  drawParticles()
  requestAnimationFrame(loop)
}

export function BubbleLoader({width, height, bgColor, startLevel = 70}) {
  const canvasRef = useRef()
  const imageRef = useRef()
  const [level, setLevel] = useState(startLevel)
  const colorsRef = useRef(levels[level - 1].colors)

  const onResize = useCallback(
    debounce(() => {
      imageRect = imageRef.current.getBoundingClientRect()
    }, 500),
    []
  )

  useEffect(() => {
    if (!canvasRef.current) return
    w = window.innerWidth
    h = window.innerHeight
    canvasRef.current.setAttribute('width', w)
    canvasRef.current.setAttribute('height', h)
    ctx = canvasRef.current.getContext('2d')
    imageRect = imageRef.current.getBoundingClientRect()
    isActive = true
    loop()

    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize)
    return () => {
      isActive = false
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize)
    }
  }, [])

  return (
    <div style={{width, height}}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      />

      <div
        className={styles.fadedImageContainer}
        style={{
          animation: 'target-loader 2.5s infinite',
          '--bg-color': bgColor,
        }}
        onAnimationIteration={() => {
          spawnParticles(canvasRef.current.getContext('2d'), colorsRef.current)
        }}
      >
        <img
          ref={imageRef}
          onClick={() => {
            const l = random(1, 200)
            setLevel(l)
            colorsRef.current = levels[l - 1].colors
          }}
          src={`/targets/${level}.png`}
          alt=""
          style={{
            aspectRatio: '4 / 3',
            width: width ? width : 400,
            height: height ? height : 'auto',
            borderRadius: '50%',
          }}
        />
      </div>
    </div>
  )
}

export const BubbleLoaderWithText = ({children, ...props}) => {
  return (
    <VStack align="center" gap="0rem">
      <BubbleLoader {...props} />
      <Text mono={false} size={4} weight={600} appearance="subdued">
        {children}
      </Text>
    </VStack>
  )
}
